import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, authAPI } from '../services/api';
import toast from 'react-hot-toast';


const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => {
    try {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      if (token && userData) {
        const parsed = JSON.parse(userData);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return parsed;
      }
    } catch (error) {
      console.error('🔐 AuthContext: Error initializing user data:', error);
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
    }
    return null;
  });

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      return Boolean(token && userData);
    } catch {
      return false;
    }
  });

  const [loading] = useState(false);

  // Sync token header if already logged in
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  const login = useCallback(async (email, password, role = 'supervisor') => {
    try {
      console.log('🔐 AuthContext: Login attempt started', { email, role });

      console.log('🔐 AuthContext: About to call authAPI.login...');
      const response = await authAPI.login({ email, password }, role);
      
      console.log('🔐 AuthContext: Full API response:', response);
      console.log('🔐 AuthContext: Response data:', response.data);
      console.log('🔐 AuthContext: Response status:', response.status);
      
      // Handle the response structure from your backend
      const { token, message, admin, employee, ...otherData } = response.data;
      
      console.log('🔐 AuthContext: Extracted data:', { token, message, admin, employee, otherData });
      
      // Extract user data from the appropriate property based on role
      let userData;
      if (role === 'admin' && admin) {
        userData = admin;
      } else if (role === 'employee' && employee) {
        userData = employee;
      } else {
        userData = otherData;
      }
      
      // Ensure userData exists
      if (!userData) {
        console.error('🔐 AuthContext: No user data found in response');
        throw new Error('Invalid response from server');
      }
      
      console.log('🔐 AuthContext: Selected userData:', userData);
      
      // Normalize roles from backend to frontend format
      let mappedUserData;
      if (role === 'admin') {
        // Admin logins provide role as 'superadmin' or 'readonly'
        const adminType = userData.role === 'superadmin' ? 'super' : userData.role === 'readonly' ? 'readonly' : null;
        mappedUserData = {
          ...userData,
          role: 'admin',
          adminType,
        };
      } else if (role === 'employee') {
        // Employee logins
        mappedUserData = {
          ...userData,
          role: 'employee',
          userType: 'employee',
          adminType: null,
        };
      } else {
        // Supervisor/manager logins may provide userType
        const normalizedRole = userData.userType === 'manager' ? 'manager' : 'supervisor';
        mappedUserData = {
          ...userData,
          role: normalizedRole,
          adminType: null,
        };
      }
      
      // Store token and user data
      localStorage.setItem('authToken', token);
      localStorage.setItem('userData', JSON.stringify(mappedUserData));
      
      console.log('🔐 AuthContext: Stored in localStorage:', {
        token: token ? 'exists' : 'missing',
        userData: mappedUserData
      });
      
      // Set API default headers
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      console.log('🔐 AuthContext: Login successful, setting user state');
      setUser(mappedUserData);
      setIsAuthenticated(true);
      
      toast.success(`Welcome back, ${mappedUserData.name}!`);
      
      // Redirect based on the actual user role
      let redirectPath;
      console.log('🔐 AuthContext: mappedUserData for redirect:', mappedUserData);
      console.log('🔐 AuthContext: mappedUserData.role:', mappedUserData.role);
      console.log('🔐 AuthContext: mappedUserData.userType:', mappedUserData.userType);
      
      if (mappedUserData.adminType) {
        redirectPath = '/admin/dashboard';
      } else if (mappedUserData.role === 'employee') {
        redirectPath = '/employee/overview';
      } else {
        redirectPath = '/manager/dashboard';
      }
      navigate(redirectPath, { replace: true });
      return { success: true };
    } catch (error) {
      console.error('🔐 AuthContext: Login error occurred:', error);
      console.error('🔐 AuthContext: Error response:', error.response);
      console.error('🔐 AuthContext: Error message:', error.message);
      
      const message = error.response?.data?.message || 'Login failed. Please try again.';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [navigate]);

  const logout = useCallback(() => {
    // Clear local storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    
    // Clear API headers
    delete api.defaults.headers.common['Authorization'];
    
    // Reset state
    setUser(null);
    setIsAuthenticated(false);
    
    toast.success('Logged out successfully');
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('userData', JSON.stringify(userData));
  }, []);

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    updateUser,
  }), [user, isAuthenticated, loading, login, logout, updateUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
